import { Link } from "react-router-dom";
import { ArrowRight, Minus, Plus, Trash2 } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { money } from "../lib/api";
import Seo from "../components/Seo";

export default function CartPage() {
  const { cart, updateQty } = useStore();
  const items = cart.items || [];
  const threshold = cart.free_shipping_threshold_cents || 0;
  const remaining = threshold > 0 ? Math.max(0, threshold - cart.subtotal_cents) : 0;

  return (
    <div className="bg-candy-mist min-h-screen">
      <Seo title="Your Cart" path="/cart" noindex />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h1 data-testid="cart-page-heading" className="font-display font-semibold text-3xl sm:text-4xl text-center">Your cozy bag</h1>
        {items.length === 0 ? (
          <div className="text-center py-20" data-testid="cart-page-empty">
            <p className="text-5xl mb-4" aria-hidden="true">🧺</p>
            <h2 className="font-display font-semibold text-2xl">Your bag is empty</h2>
            <p className="text-muted mt-2">Fill it with something soft and snuggly.</p>
            <Link to="/shop" data-testid="cart-page-shop-cta" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-candy text-white font-bold shadow-pink hover:bg-candy-deep transition-colors duration-200">
              Browse creations <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2 space-y-4">
              {items.map((it) => (
                <div key={`${it.product_id}-${it.variant_label}`} data-testid={`cart-page-item-${it.slug}`} className="flex gap-4 items-center bg-white rounded-3xl border border-candy-soft/70 p-4">
                  <img src={it.image} alt="" className="w-20 h-20 rounded-2xl object-cover bg-candy-soft" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${it.slug}`} className="font-display font-semibold text-lg hover:text-candy-deep transition-colors duration-200">{it.title}</Link>
                    {it.variant_label && <p className="text-xs text-muted font-bold mt-0.5">{it.variant_label}</p>}
                    <p className="font-bold text-candy-deep mt-1">{money(it.price_cents)}</p>
                  </div>
                  <div className="flex items-center gap-1 bg-candy-mist rounded-full px-1 py-1">
                    <button data-testid={`cart-page-qty-minus-${it.slug}`} onClick={() => updateQty(it.product_id, it.variant_label, it.qty - 1)} aria-label="Decrease quantity" className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-candy-glow transition-colors duration-200"><Minus className="w-4 h-4" /></button>
                    <span className="w-7 text-center font-bold" data-testid={`cart-page-qty-${it.slug}`}>{it.qty}</span>
                    <button data-testid={`cart-page-qty-plus-${it.slug}`} onClick={() => updateQty(it.product_id, it.variant_label, it.qty + 1)} disabled={it.qty >= it.max_stock} aria-label="Increase quantity" className="w-8 h-8 rounded-full bg-white flex items-center justify-center hover:bg-candy-glow transition-colors duration-200 disabled:opacity-40"><Plus className="w-4 h-4" /></button>
                  </div>
                  <button data-testid={`cart-page-remove-${it.slug}`} onClick={() => updateQty(it.product_id, it.variant_label, 0)} aria-label={`Remove ${it.title}`} className="p-2 text-muted hover:text-candy-deep transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <aside className="bg-white rounded-3xl border border-candy-soft/70 p-6 space-y-3 md:sticky md:top-28" data-testid="cart-summary">
              {threshold > 0 && (
                <div data-testid="cart-page-free-shipping">
                  <p className="text-xs font-bold mb-1.5">{remaining > 0 ? `${money(remaining)} away from free shipping` : "You've unlocked free shipping!"}</p>
                  <div className="h-2 rounded-full bg-candy-mist overflow-hidden">
                    <div className="h-full bg-candy rounded-full transition-all duration-500" style={{ width: `${Math.min(100, (cart.subtotal_cents / threshold) * 100)}%` }} />
                  </div>
                </div>
              )}
              <div className="flex justify-between text-sm font-bold text-ink/70"><span>Subtotal</span><span data-testid="cart-page-subtotal">{money(cart.subtotal_cents)}</span></div>
              <div className="flex justify-between text-sm font-bold text-ink/70"><span>Shipping</span><span data-testid="cart-page-shipping">{cart.shipping_cents === 0 ? "Free!" : money(cart.shipping_cents)}</span></div>
              <div className="flex justify-between font-display font-semibold text-xl border-t border-candy-soft pt-3"><span>Total</span><span data-testid="cart-page-total">{money(cart.total_cents)}</span></div>
              <Link to="/checkout" data-testid="cart-page-checkout-button" className="block text-center w-full py-3.5 rounded-full bg-candy hover:bg-candy-deep text-white font-bold shadow-pink transition-colors duration-200">
                Checkout
              </Link>
              <p className="text-[11px] text-muted text-center font-semibold">Secure checkout powered by Stripe — currently in test mode.</p>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
